import { useLayoutEffect, useRef, useState } from 'react';

import libravatarImage from '../modules/libravatar-image.mjs';
import formatName from '../modules/format-name';
import { getRoomNumber, getRoomFloor } from '../modules/roomMatching';

const dayDuration = 10 * 60 * 60 * 1000;

// The scrollable timeline track is rendered at a fixed pixel width, wider
// than the visible pane — that's what makes horizontal scrolling meaningful.
const TIMELINE_WIDTH_PX = 1600;
const SCROLL_LOOKBACK_MS = 1.25 * 60 * 60 * 1000;

const swedishNameFixes = {
    asa: 'Åsa',
    goran: 'Göran',
    hakan: 'Håkan',
    soren: 'Sören',
    janarne: 'Jan-Arne',
};

const formatHourMinute = (date) => {
    const hour = String(date.getHours()).padStart(2, '0');
    const minute = String(date.getMinutes()).padStart(2, '0');

    return `${hour}:${minute}`;
};

const formatOrganizer = (email) => {
    if (!email) return { name: 'Okänd bokare', identity: null };
    const localPart = email.split('@')[0];
    if (!localPart.includes('.')) {
        const display = localPart.charAt(0).toUpperCase() + localPart.slice(1).toLowerCase();
        return { name: display, identity: localPart.toLowerCase() };
    }
    const parts = localPart.split(/[._]/).filter(Boolean);
    const name = parts
        .map(part => {
            const lower = part.toLowerCase();
            if (swedishNameFixes[lower]) return swedishNameFixes[lower];
            return part
                .split('-')
                .map(sub => sub.charAt(0).toUpperCase() + sub.slice(1).toLowerCase())
                .join('-');
        })
        .join(' ');
    return { name, identity: localPart.toLowerCase() };
};

const TOOLTIP_GAP_PX = 14;
const TOOLTIP_VIEWPORT_MARGIN_PX = 8;

// The timeline scrolls horizontally, and an overflow-x container clips on
// both axes — so a single tooltip is rendered outside the scroll area,
// position: fixed, and placed here against the viewport. (Keeping it inside
// the scroll area also left Safari not repainting it on later hovers.)
const positionTooltip = (tooltip, anchor) => {
    const scrollArea = anchor.closest('.rooms-scroll-area');
    const anchorRect = anchor.getBoundingClientRect();
    const visibleRect = scrollArea ? scrollArea.getBoundingClientRect() : anchorRect;
    const visibleLeft = Math.max(anchorRect.left, visibleRect.left);
    const visibleRight = Math.min(anchorRect.right, visibleRect.right);
    const anchorCenter = (visibleLeft + visibleRight) / 2;

    const halfWidth = tooltip.offsetWidth / 2;
    const minCenter = halfWidth + TOOLTIP_VIEWPORT_MARGIN_PX;
    const maxCenter = window.innerWidth - halfWidth - TOOLTIP_VIEWPORT_MARGIN_PX;
    const tooltipCenter = Math.min(Math.max(anchorCenter, minCenter), maxCenter);

    const fitsAbove = anchorRect.top - tooltip.offsetHeight - TOOLTIP_GAP_PX >= TOOLTIP_VIEWPORT_MARGIN_PX;

    tooltip.classList.toggle('tooltip-below', !fitsAbove);
    tooltip.style.left = `${tooltipCenter}px`;
    tooltip.style.top = `${fitsAbove ? anchorRect.top : anchorRect.bottom}px`;
    tooltip.style.setProperty('--tooltip-arrow-shift', `${anchorCenter - tooltipCenter}px`);
};

function Rooms ({bookingsToday: rooms, allDevices = []}) {
    const scrollAreaRef = useRef(null);
    const tooltipRef = useRef(null);
    const [activeTooltip, setActiveTooltip] = useState(null);

    useLayoutEffect(() => {
        if (activeTooltip && tooltipRef.current) {
            positionTooltip(tooltipRef.current, activeTooltip.anchor);
        }
    }, [activeTooltip]);

    const tooltipCountRef = useRef(0);

    const showTooltip = (event, details) => {
        tooltipCountRef.current += 1;
        // A fresh key per hover remounts the tooltip so its fade-in replays
        // when moving straight from one booking to the next.
        setActiveTooltip({ ...details, anchor: event.currentTarget, key: tooltipCountRef.current });
    };

    const hideTooltip = () => setActiveTooltip(null);

    // A device isn't tagged with which meeting room it's in directly — but its
    // WiFi access point location (e.g. "314") is the same room number that
    // appears at the end of a room's identifier (room-floor3-stendahls-se-314 →
    // Aladdin). Matching on that number is what lets someone's WiFi presence
    // show up as an occupant chip on the right room row, for both mock and
    // real Unicator data.
    const roomNumberByIdentifier = new Map(
        rooms.map( ( room ) => [ getRoomNumber( room.identifier ), room.identifier ] )
    );

    const occupantsByRoom = allDevices.reduce( ( map, device ) => {
        const roomIdentifier = device.locator && roomNumberByIdentifier.get( device.ap.location );

        if ( !roomIdentifier ) {
            return map;
        }

        if ( !map.has( roomIdentifier ) ) {
            map.set( roomIdentifier, [] );
        }

        map.get( roomIdentifier ).push( device );

        return map;
    }, new Map() );

    const isMockData = new URLSearchParams(window.location.search).get('mock') === '1';

    const startOfToday = new Date().setHours(8, 0, 0, 0);
    // In mock mode, pin "now" to midday so the right-now line always lands
    // somewhere meaningful on the timeline, regardless of when you're testing.
    const now = isMockData ? new Date().setHours(12, 0, 0, 0) : new Date().getTime();

    const rightNowPosition = (now - startOfToday) / dayDuration * 100;
    const clampedPosition = Math.min(Math.max(rightNowPosition, 0), 100);

    // A room counts as available for a short meeting only if it's free for
    // the whole window from 1 to 25 minutes from now — not just free this
    // instant. in1Minute (not now) is used so a meeting ending in the next
    // minute still counts the room as available right away.
    const in1Minute = now + (1 * 60 * 1000);
    const in25Minutes = now + (25 * 60 * 1000);

    const isRoomAvailableSoon = (room) => {
        const hasConflict = room.bookings.some((booking) => {
            let startTime = booking.start;
            let endTime = booking.end;

            if (new Date(endTime) - new Date(startTime) === 86400000 && startTime.endsWith('Z') && endTime.endsWith('Z')) {
                endTime = endTime.slice(0, -1);
                startTime = startTime.slice(0, -1);
            }

            const bookingStart = new Date(startTime).getTime();
            const bookingEnd = new Date(endTime).getTime();

            return bookingStart < in25Minutes && bookingEnd > in1Minute;
        });

        return !hasConflict;
    };

    const availableRooms = rooms.filter((room) => isRoomAvailableSoon(room));

    // Gridlines across the timeline (09:00–17:00, every 30 min) — a lightweight
    // visual reference so a slot's rough time can be read without zooming in on
    // its label. Half-hour marks are drawn thinner than hour marks.
    const endOfToday = startOfToday + dayDuration;
    const halfHourMs = 30 * 60 * 1000;
    const hourMarks = [];

    for (
        let markTime = new Date(startOfToday).setMinutes(0, 0, 0) + halfHourMs;
        markTime <= endOfToday;
        markTime += halfHourMs
    ) {
        const markDate = new Date(markTime);

        hourMarks.push({
            key: markTime,
            hour: markDate.getHours(),
            isHour: markDate.getMinutes() === 0,
            position: (markTime - startOfToday) / dayDuration * 100,
        });
    }

    // On load, scroll so "now minus 2 hours" sits at the left edge of the
    // visible pane — a bit of recent history stays visible, the rest of the
    // day is reachable by scrolling right.
    useLayoutEffect(() => {
        const scrollArea = scrollAreaRef.current;

        if (!scrollArea) {
            return;
        }

        const lookbackPercent = (SCROLL_LOOKBACK_MS / dayDuration) * 100;
        const scrollToPercent = Math.max(0, rightNowPosition - lookbackPercent);

        scrollArea.scrollLeft = (scrollToPercent / 100) * TIMELINE_WIDTH_PX;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div
            className = "rooms"
        >
            {availableRooms.length > 0 && (
                <div className="available-rooms">
                    <h3>Lediga rum för kort möte</h3>
                    <div className="available-rooms-links">
                        {availableRooms.map(room => (
                            <a
                                key={room.identifier}
                                href={`https://intranet.stendahls.dev/praktisk-information/vad-finns-var/kartor/?vaning=${getRoomFloor(room.identifier)}&typ=motesrum`}
                                className="available-room-link"
                            >
                                <span className="available-room-name">{room.displayName}</span>
                                <span className="available-room-number">{getRoomNumber(room.identifier)}</span>
                            </a>
                        ))}
                    </div>
                </div>
            )}
            <div className="rooms-timeline-container">
                <div className="rooms-body">
                    <div className="room-labels-column">
                        {rooms.map(room => {
                        const occupants = occupantsByRoom.get(room.identifier) || [];

                        return <div
                            key = {room.identifier}
                            className = "room-label-row"
                        >
                            <div
                                className = "room-label-column"
                            >
                                <h2>
                                    <a
                                        href = {`https://intranet.stendahls.dev/praktisk-information/vad-finns-var/kartor/?vaning=${getRoomFloor(room.identifier)}&typ=motesrum`}
                                    >
                                        {room.displayName} <span className="room-number">{getRoomNumber(room.identifier)}</span>
                                    </a>
                                </h2>
                                <div
                                    className = "room-occupants"
                                >
                                    { occupants.map((device) => (
                                        <div
                                            className = "room-occupant-chip"
                                            key = { device.mac }
                                            data-identity = { device.identity }
                                            title = { formatName( device.identity ) }
                                        >
                                            <img
                                                alt = { device.identity }
                                                src = { libravatarImage(`${device.identity}@stendahls.se`) }
                                            />
                                        </div>
                                    )) }
                                </div>
                            </div>
                        </div>;
                        })}
                    </div>
                    <div
                        className = "rooms-scroll-area"
                        ref = { scrollAreaRef }
                        onScroll = { hideTooltip }
                    >
                        <div
                            className = "rooms-scroll-inner"
                            style = {{ width: `${TIMELINE_WIDTH_PX}px` }}
                        >
                            <div
                                className = "right-now-line"
                                style = {{ left: `${clampedPosition}%` }}
                            />

                            {rooms.map(room => {
                            return <div
                                key = {room.identifier}
                                className = "room-track-row"
                            >
                                <div
                                    className = "room-wrapper"
                                >
                                { hourMarks.map((mark) => (
                                    <div
                                        className = { `hour-gridline${ mark.isHour ? ' hour-gridline-hour' : ' hour-gridline-half' }` }
                                        key = { mark.key }
                                        style = {{ left: `${mark.position}%` }}
                                    >
                                        { room === rooms[0] && mark.isHour &&
                                            <span className="hour-gridline-label">
                                                { `${mark.hour}:00` }
                                            </span>
                                        }
                                    </div>
                                )) }
                                {room.bookings.map(booking => {
                                    if(new Date(booking.end) - new Date(booking.start) === 86400000 && booking.start.endsWith('Z') && booking.end.endsWith('Z')) {
                                        // All-day events are stored as YYYY-MM-DDT00:00:00Z (UTC) which becomes 1 hour wrong during winter (normal) time. Getting rid of the Z fixes this!
                                        booking.end = booking.end.slice(0, -1);
                                        booking.start = booking.start.slice(0, -1);
                                    }
                                    const startPosition = (new Date(booking.start).getTime() - startOfToday) / dayDuration * 100;
                                    const width = (new Date(booking.end).getTime() - new Date(booking.start).getTime()) / dayDuration * 100;

                                    if(startPosition > 100){
                                        console.log(booking);

                                        return null;
                                    }

                                    const startTime = new Date(booking.start).toLocaleTimeString('sv-SE', { timeStyle: 'short' });
                                    const endTime = new Date(booking.end).toLocaleTimeString('sv-SE', { timeStyle: 'short' });

                                    const mailtoHref = `mailto:${booking.organizer}?subject=Angående%20din%20rumsbokning%20i%20${room.displayName}%20(${getRoomNumber(room.identifier)})%20kl%20${startTime}`;

                                    const { name: organizerName, identity } = formatOrganizer(booking.organizer);
                                    const avatarUrl = identity ? libravatarImage(`${identity}@stendahls.se`, 96) : null;

                                    const rawSubject = (booking.subject || '').trim();
                                    const normalize = (s) => s.toLowerCase().replace(/\s+/g, ' ').trim();
                                    const subjectIsJustName = rawSubject && normalize(rawSubject) === normalize(organizerName);
                                    const displaySubject = rawSubject && !subjectIsJustName ? rawSubject : null;

                                    const durationMs = new Date(booking.end).getTime() - new Date(booking.start).getTime();
                                    const durationMinutes = Math.round(durationMs / 60000);
                                    const durationLabel = durationMinutes >= 60
                                        ? `${Math.floor(durationMinutes / 60)} h${durationMinutes % 60 ? ` ${durationMinutes % 60} min` : ''}`
                                        : `${durationMinutes} min`;

                                    const clampedLeft = Math.max(startPosition, 0);
                                    const clampedWidth = Math.min(width, 100 - clampedLeft);
                                    const bookingStartMs = new Date(booking.start).getTime();
                                    const bookingEndMs = new Date(booking.end).getTime();
                                    const isPast = bookingEndMs < now;
                                    const isOngoing = bookingStartMs <= now && now < bookingEndMs;

                                    const startHm = formatHourMinute(new Date(booking.start));
                                    const endHm = formatHourMinute(new Date(booking.end));

                                    const tooltipDetails = {
                                        avatarUrl,
                                        organizerName,
                                        organizer: booking.organizer,
                                        displaySubject,
                                        startTime,
                                        endTime,
                                        durationLabel,
                                    };

                                    return <div
                                        className = { `booking-wrapper${ isPast ? ' booking-past' : '' }${ isOngoing ? ' booking-ongoing' : '' }` }
                                        key = {`${booking.roomIdentifier}-${booking.start}`}
                                        style = {{
                                            left: `${clampedLeft}%`,
                                            width: `${clampedWidth}%`,
                                        }}
                                    >
                                        <a
                                            href={mailtoHref}
                                            onMouseEnter={(event) => showTooltip(event, tooltipDetails)}
                                            onFocus={(event) => showTooltip(event, tooltipDetails)}
                                            onMouseLeave={hideTooltip}
                                            onBlur={hideTooltip}
                                        >
                                            { !isPast &&
                                                <span className="booking-time">
                                                    { `${ startHm }–${ endHm }` }
                                                </span>
                                            }
                                        </a>
                                    </div>;
                                })}
                                </div>
                            </div>;
                            })}
                        </div>
                    </div>
                </div>
            </div>
            {activeTooltip && (
                <span className="tooltiptext" role="tooltip" ref={tooltipRef} key={activeTooltip.key}>
                    <span className="tooltip-header">
                        {activeTooltip.avatarUrl && (
                            <span
                                className="tooltip-avatar"
                                style={{ backgroundImage: `url(${activeTooltip.avatarUrl})` }}
                                aria-hidden="true"
                            />
                        )}
                        <span className="tooltip-identity">
                            <span className="tooltip-name">{activeTooltip.organizerName}</span>
                            {activeTooltip.organizer && (
                                <span className="tooltip-email">{activeTooltip.organizer}</span>
                            )}
                        </span>
                    </span>
                    {activeTooltip.displaySubject && (
                        <span className="tooltip-subject">
                            <span className="tooltip-subject-label">Ämne</span>
                            <span className="tooltip-subject-text">{activeTooltip.displaySubject}</span>
                        </span>
                    )}
                    <span className="tooltip-meta">
                        <span className="tooltip-time">
                            {activeTooltip.startTime}<span className="tooltip-dash">–</span>{activeTooltip.endTime}
                        </span>
                        <span className="tooltip-duration">{activeTooltip.durationLabel}</span>
                    </span>
                    <span className="tooltip-hint">
                        Klicka för att mejla
                    </span>
                </span>
            )}
        </div>
    )
}

export default Rooms;
